// Chat module dashboard — shown in the main area of /chat when no room is open.
// Surfaces chat status (채팅 현황), the AI persona roster (AI 리스트), and the
// member directory (멤버 리스트).
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

type RoomType = "DIRECT" | "GROUP" | "DEPARTMENT" | "PROJECT" | "OPEN";
type Room = { id: number; type: RoomType; name: string | null; member_count: number; description?: string | null };
type Persona = {
  id: number; name: string; slug: string; avatar_emoji: string;
  system_prompt: string; model_id: number | null; user_id: number | null; is_active: boolean;
};
type Member = {
  id: number; name: string; username?: string | null;
  department_name: string | null; position_name: string | null;
  status: string; profile_image: string | null;
};

const ROOM_ICON: Record<RoomType, string> = {
  GROUP: "#", DEPARTMENT: "🏢", DIRECT: "💬", PROJECT: "📁", OPEN: "🌐",
};
const ROOM_TYPE_LABEL: Record<RoomType, string> = {
  GROUP: "그룹", DEPARTMENT: "부서", DIRECT: "DM", PROJECT: "프로젝트", OPEN: "공개",
};

const AVATAR_COLORS = ["#6E6FCF", "#0891B2", "#16A34A", "#D97706", "#DB2777", "#7C3AED", "#0F766E", "#DC2626"];
function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ─── Presence ──────────────────────────────────────────────────────────────────
// last_seen 나이로 3단계: 접속중(초록) / 1시간 이내(노랑) / 미접속(회색).
const PRESENCE_ONLINE_MS = 70_000;        // 하트비트 60초 + 여유
const PRESENCE_AWAY_MS = 60 * 60_000;     // 1시간
const PRESENCE_GRAY = "var(--border)";
function presenceStyle(lastSeen: number | undefined): { color: string; title: string; online: boolean } {
  if (lastSeen == null) return { color: PRESENCE_GRAY, title: "미접속", online: false };
  const age = Date.now() - lastSeen;
  if (age <= PRESENCE_ONLINE_MS) return { color: "var(--ok, #16A34A)", title: "접속 중", online: true };
  if (age <= PRESENCE_AWAY_MS) {
    const mins = Math.max(1, Math.round(age / 60_000));
    return { color: "#F59E0B", title: `${mins}분 전 접속`, online: false };
  }
  return { color: PRESENCE_GRAY, title: "미접속", online: false };
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function ChatDashboard() {
  const nav = useNavigate();
  const { user: authUser } = useAuth();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [presence, setPresence] = useState<Record<number, number>>({}); // uid → last_seen(ms)
  const [, setNowTick] = useState(0);                                   // 색 갱신용 타이머 틱
  const [loading, setLoading] = useState(true);
  const [dmBusy, setDmBusy] = useState(false);

  // 멤버 클릭/우클릭 → 그 사람과의 1:1 DM 방을 열거나 새로 만들어 이동
  const openDm = async (memberId: number) => {
    if (dmBusy) return;
    if (authUser?.id === memberId) { toast.info("자기 자신과는 DM을 만들 수 없습니다"); return; }
    setDmBusy(true);
    try {
      const r = await api.post<Room>(`/chat/dm/${memberId}`);
      nav(`/chat?room=${r.data.id}`);
    } catch (e: any) {
      toast.error("DM 열기 실패", e?.response?.data?.error?.message ?? "");
    } finally { setDmBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get<Room[]>("/chat/rooms"),
      api.get<Persona[]>("/chat/personas"),
      api.get<any>("/users", { params: { size: 100, status: "ACTIVE" } }),
    ]).then(([r, p, u]) => {
      if (!alive) return;
      if (r.status === "fulfilled") setRooms(r.value.data);
      if (p.status === "fulfilled") setPersonas(p.value.data);
      if (u.status === "fulfilled") { const b: any = u.value.data; setMembers(Array.isArray(b) ? b : (b?.data ?? b?.items ?? [])); }
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  // 프레즌스: 마운트 시 + 30초마다 갱신. 별도로 30초 틱으로 경과시간에 따른 색 재계산.
  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get<{ user_id: number; last_seen: string }[]>("/chat/presence")
        .then((r) => { if (alive) setPresence(Object.fromEntries(r.data.map((p) => [p.user_id, new Date(p.last_seen).getTime()]))); })
        .catch(() => {});
    load();
    const iv = window.setInterval(() => { load(); setNowTick((t) => t + 1); }, 30_000);
    return () => { alive = false; window.clearInterval(iv); };
  }, []);

  const channels = rooms.filter((r) => r.type !== "DIRECT");
  const dms = rooms.filter((r) => r.type === "DIRECT");
  const activePersonas = personas.filter((p) => p.is_active);

  const openRoom = (id: number) => nav(`/chat?room=${id}`);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px 48px" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--p-600)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, color: "#fff" }}>T</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Tim Box 채팅</h1>
              <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 1 }}>채팅 현황 · AI 페르소나 · 멤버</div>
            </div>
          </div>
          {/* 데스크톱·모바일 앱 받기 — 공개 다운로드 페이지(/download)를 새 탭으로 연다.
              설치하면 새 버전은 앱이 스스로 받는다(자동 업데이트). */}
          <a
            href="/download"
            target="_blank"
            rel="noopener noreferrer"
            title="데스크톱(Windows)·안드로이드 앱 다운로드"
            style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              background: "var(--p-600)", color: "#fff", textDecoration: "none",
              fontSize: 13, fontWeight: 700, padding: "9px 15px", borderRadius: 10,
              boxShadow: "0 2px 8px rgba(91,92,184,.25)", whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 15 }}>⬇</span> 앱 다운로드
          </a>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <Kpi icon="#" label="채널" value={channels.length} accent="var(--m-collab, #0891B2)" />
        <Kpi icon="💬" label="다이렉트 메시지" value={dms.length} accent="var(--p-500)" />
        <Kpi icon="🤖" label="AI 페르소나" value={activePersonas.length} accent="#7C3AED" />
        <Kpi icon="👥" label="전체 멤버" value={members.length} accent="var(--m-hrm, #16A34A)" />
      </div>

      {/* Two-column layout: chat status (wide) + AI list (narrow) */}
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16, marginBottom: 16, alignItems: "start" }}>

        {/* ── 채팅 현황 ── */}
        <Card
          title="채팅 현황"
          icon="💬"
          accent="var(--p-500)"
          action={<button className="t-btn t-btn-primary t-btn-sm" onClick={() => nav("/chat?modal=create")}>+ 새 채팅방</button>}
        >
          {loading ? (
            <Skeleton rows={4} />
          ) : channels.length === 0 && dms.length === 0 ? (
            <Empty text="참여 중인 채팅방이 없습니다" />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {[...channels, ...dms].slice(0, 12).map((r) => (
                <RoomTile key={r.id} room={r} onClick={() => openRoom(r.id)} />
              ))}
            </div>
          )}
        </Card>

        {/* ── AI 리스트 ── */}
        <Card
          title="AI 리스트"
          icon="🤖"
          accent="#7C3AED"
          action={<button className="t-btn t-btn-outline t-btn-sm" onClick={() => nav("/chat?modal=ai")}>관리</button>}
        >
          {loading ? (
            <Skeleton rows={3} />
          ) : personas.length === 0 ? (
            <Empty text="등록된 AI 페르소나가 없습니다" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {personas.map((p) => (
                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 8px", borderRadius: 8, background: "var(--panel)" }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: "rgba(124,58,237,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                    {p.avatar_emoji || "🤖"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>@{p.slug}</div>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                    background: p.is_active ? "rgba(34,197,94,0.14)" : "var(--border)",
                    color: p.is_active ? "var(--ok, #16A34A)" : "var(--text-3)",
                  }}>
                    {p.is_active ? "활성" : "비활성"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ── 멤버 리스트 ── */}
      <Card title="멤버 리스트" icon="👥" accent="var(--m-hrm, #16A34A)"
            action={
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: "var(--text-3)" }}>
                  <LegendDot c="var(--ok, #16A34A)" label="접속중" />
                  <LegendDot c="#F59E0B" label="1시간 이내" />
                  <LegendDot c="var(--border)" label="미접속" />
                </div>
                <button className="t-btn t-btn-outline t-btn-sm" onClick={() => nav("/directory")}>전체 보기</button>
              </div>
            }>
        {loading ? (
          <Skeleton rows={3} />
        ) : members.length === 0 ? (
          <Empty text="멤버 정보가 없습니다" />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6 }}>
            {members.slice(0, 24).map((m) => {
              const isSelf = authUser?.id === m.id;
              const ps = presenceStyle(presence[m.id]);
              return (
                <div key={m.id}
                  onClick={() => !isSelf && openDm(m.id)}
                  onContextMenu={(e) => { if (!isSelf) { e.preventDefault(); openDm(m.id); } }}
                  title={isSelf ? "" : "클릭하여 1:1 DM"}
                  onMouseEnter={(e) => { if (!isSelf) (e.currentTarget as HTMLElement).style.background = "var(--hover)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                  style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 8px", borderRadius: 8, cursor: isSelf ? "default" : "pointer", transition: "background 0.12s" }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, background: avatarColor(m.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>
                    {m.name.slice(0, 2)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}{isSelf && <span style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 400 }}> · 나</span>}</div>
                    {(m.department_name || m.position_name) && (
                      <div style={{ fontSize: 10.5, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {[m.department_name, m.position_name].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div title={ps.title} style={{
                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: ps.color,
                    boxShadow: ps.online ? "0 0 0 2px color-mix(in srgb, var(--ok, #16A34A) 30%, transparent)" : undefined,
                  }} />
                </div>
              );
            })}
          </div>
        )}
        {members.length > 24 && (
          <div style={{ textAlign: "center", marginTop: 8, fontSize: 12, color: "var(--text-3)" }}>
            외 {members.length - 24}명
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────────

function Kpi({ icon, label, value, accent }: { icon: string; label: string; value: number; accent: string }) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, background: "var(--bg-0)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: `color-mix(in srgb, ${accent} 14%, transparent)`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>{value}</div>
      </div>
    </div>
  );
}

function Card({ title, icon, accent, action, children }: {
  title: string; icon: string; accent: string; action?: ReactNode; children: ReactNode;
}) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, background: "var(--bg-0)", padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ color: accent }}>{icon}</span> {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function RoomTile({ room, onClick }: { room: Room; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 9, padding: "8px 10px",
        borderRadius: 8, cursor: "pointer", transition: "background 0.12s",
        background: hovered ? "var(--hover)" : "var(--panel)",
      }}
    >
      <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: "color-mix(in srgb, var(--p-500) 16%, transparent)", color: "var(--p-500)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
        {(room as any).icon || ROOM_ICON[room.type] || "#"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {room.name || `방 ${room.id}`}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-3)" }}>
          {ROOM_TYPE_LABEL[room.type]} · {room.member_count}명
        </div>
      </div>
    </div>
  );
}

function LegendDot({ c, label }: { c: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: c, flexShrink: 0 }} />
      {label}
    </span>
  );
}

function Empty({ text }: { text: string }) {
  return <div style={{ fontSize: 12, color: "var(--text-3)", padding: "16px 0", textAlign: "center" }}>{text}</div>;
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ height: 38, borderRadius: 8, background: "var(--panel)", opacity: 0.6 }} />
      ))}
    </div>
  );
}
